import moment from 'moment-timezone'

import { toUtcIsoInstant } from './normalizeTelemetryInstants'

/**
 * Reads a value from a payload accepting either camelCase or PascalCase keys, so the normalizer is
 * resilient to whichever serializer casing the API/proxy emits (same approach as the summary payload).
 */
function pick(source, camelKey, pascalKey) {
  if (source == null) {
    return undefined
  }
  const camel = source[camelKey]
  if (camel !== undefined) {
    return camel
  }
  return source[pascalKey]
}

/** Safely parses the inline JSON snapshot; returns the parsed object plus a pretty-printed text form. */
function parseCaptureJson(jsonContent) {
  if (jsonContent == null || jsonContent === '') {
    return { json: null, jsonText: '' }
  }
  if (typeof jsonContent === 'object') {
    return { json: jsonContent, jsonText: safeStringify(jsonContent) }
  }
  try {
    const parsed = JSON.parse(jsonContent)
    return { json: parsed, jsonText: safeStringify(parsed) }
  } catch {
    return { json: null, jsonText: String(jsonContent) }
  }
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

/**
 * Human machine identity for a capture row: "osUser @ machineName" from the JSON snapshot, falling back
 * to whichever part is present, then to the device installation id, so a row is never anonymous.
 */
export function captureMachineIdentity(capture) {
  const json = capture?.json
  const user = (pick(json, 'osUserName', 'OsUserName') ?? '').toString().trim()
  const machine = (pick(json, 'machineName', 'MachineName') ?? '').toString().trim()
  if (user && machine) {
    return `${user} @ ${machine}`
  }
  if (machine) {
    return machine
  }
  if (user) {
    return user
  }
  const device = (capture?.deviceInstallationId ?? '').toString().trim()
  return device || null
}

/**
 * Normalizes the diagnostic-captures list payload into a stable, casing-agnostic shape sorted newest
 * first. Internal-global dashboard users are filtered client-side as defense-in-depth (the proxy already
 * strips them server-side), so CSMs never see internal staff captures.
 *
 * @param {*} raw API/proxy response (object with `captures`/`Captures`, or a bare array).
 * @param {{ internalGlobalDashboardUserIds?: Array<string|number> }} [options]
 * @returns {Array<object>} normalized captures, newest first.
 */
export function normalizeDiagnosticCaptures(raw, options = {}) {
  const list = Array.isArray(raw)
    ? raw
    : pick(raw, 'captures', 'Captures')

  if (!Array.isArray(list)) {
    return []
  }

  const blockedIds = new Set(
    (options.internalGlobalDashboardUserIds || [])
      .filter(id => id != null)
      .map(id => String(id))
  )

  const normalized = list
    .map(normalizeSingleCapture)
    .filter(Boolean)
    .filter(c => {
      if (c.dashboardUserId == null) {
        return true
      }
      return !blockedIds.has(String(c.dashboardUserId))
    })

  normalized.sort((a, b) => occurredAtMs(b) - occurredAtMs(a))
  return normalized
}

function normalizeSingleCapture(item) {
  if (item == null) {
    return null
  }
  const occurredAt = toUtcIsoInstant(pick(item, 'occurredAt', 'OccurredAt') ?? null)
  const { json, jsonText } = parseCaptureJson(pick(item, 'jsonContent', 'JsonContent'))

  return {
    id: pick(item, 'id', 'Id') ?? null,
    deviceInstallationId: pick(item, 'deviceInstallationId', 'DeviceInstallationId') ?? null,
    dashboardUserId: pick(item, 'dashboardUserId', 'DashboardUserId') ?? null,
    occurredAt,
    eventCode: pick(item, 'eventCode', 'EventCode') ?? '',
    severity: pick(item, 'severity', 'Severity') ?? 'Info',
    message: pick(item, 'message', 'Message') ?? null,
    hasScreenshot: Boolean(pick(item, 'hasScreenshot', 'HasScreenshot')),
    screenshotUrl: pick(item, 'screenshotUrl', 'ScreenshotUrl') ?? null,
    json,
    jsonText
  }
}

function occurredAtMs(capture) {
  const m = moment(capture?.occurredAt)
  return m.isValid() ? m.valueOf() : 0
}
